import React, { useMemo, useState } from "react";

import { FoodDetailPanel } from "./FoodDetailPanel";
import { MAX_CONCURRENT_JOBS } from "./ingestJobs";
import {
  Badge,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  upcOf,
} from "./shared";
import { CatalogSearchResult, PassioFood } from "./types";
import { CatalogApi, extractApiError } from "./useCatalogApi";

interface SearchEditTabProps {
  api: CatalogApi;
  ingestChainId: string;
  customerName?: string;
  /** Start a background research run for the queued items. */
  onSubmit: (foodInput: string, productCount: number) => Promise<void>;
  /** How many runs are already in flight (concurrency guard). */
  activeCount: number;
}

const PAGE_SIZE = 25;

// A single item queued to be re-researched. We only keep the identifying fields
// the ingest pipeline needs — it re-researches fresh data and, because the item
// already exists (same UPC/name), the review page surfaces the full merge/edit
// experience against the existing record.
interface QueueItem {
  key: string;
  name: string;
  brand: string;
  upc: string;
  /** Provenance back-refs (not sent to the chain, just for de-dup/context). */
  refCode?: string;
  id?: string;
}

// ---- research queue persistence (survives tab switches / reloads) ----
// NOTE: localStorage is a deliberate stop-gap (see ingestJobs.ts). This is
// lightweight working state (product identifiers to research), not a record of
// truth, so a simple wrapped get/set is enough.
const LS_QUEUE_KEY = "catalogManager.researchQueue";

const loadQueue = (): QueueItem[] => {
  try {
    const raw = window.localStorage.getItem(LS_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as QueueItem[]) : [];
  } catch {
    return [];
  }
};

const saveQueue = (q: QueueItem[]): void => {
  try {
    window.localStorage.setItem(LS_QUEUE_KEY, JSON.stringify(q));
  } catch {
    /* ignore */
  }
};

const uid = (): string =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const buildQueueInput = (items: QueueItem[]): string =>
  JSON.stringify(
    items.map((i) => ({
      name: i.name.trim() || undefined,
      brand: i.brand.trim() || undefined,
      upc: i.upc.trim() || undefined,
    }))
  );

const SearchEditTab: React.FC<SearchEditTabProps> = ({
  api,
  ingestChainId,
  customerName,
  onSubmit,
  activeCount,
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CatalogSearchResult[]>([]);
  const [page, setPage] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");

  const [selectedRefCode, setSelectedRefCode] = useState("");
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState("");

  const [selected, setSelected] = useState<PassioFood | null>(null);
  const [draft, setDraft] = useState<PassioFood | null>(null);
  // Edited since the last re-check -> auto re-check when the editor is closed.
  const [dirtySinceRecheck, setDirtySinceRecheck] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");
  const [rechecking, setRechecking] = useState(false);

  // ---- research queue ----
  const [queue, setQueue] = useState<QueueItem[]>(() => loadQueue());
  const [queueOpen, setQueueOpen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [queueMsg, setQueueMsg] = useState("");
  const [queueErr, setQueueErr] = useState("");

  const atCapacity = activeCount >= MAX_CONCURRENT_JOBS;

  const setAndSaveQueue = (next: QueueItem[]) => {
    setQueue(next);
    saveQueue(next);
  };

  // Add an item to the research queue, skipping obvious duplicates.
  const addToQueue = (item: Omit<QueueItem, "key">): boolean => {
    let added = false;
    setQueue((q) => {
      const dupe = q.some(
        (x) =>
          (item.refCode && x.refCode === item.refCode) ||
          (item.id && x.id === item.id) ||
          (item.upc.trim() && x.upc.trim() === item.upc.trim())
      );
      if (dupe) return q;
      added = true;
      const next = [...q, { key: uid(), ...item }];
      saveQueue(next);
      return next;
    });
    return added;
  };

  const queueSearchResult = (r: CatalogSearchResult) => {
    const ok = addToQueue({
      name: r.displayName || "",
      brand: r.brandName || "",
      upc: r.productCode || "",
      refCode: r.refCode,
    });
    setQueueMsg(
      ok
        ? `Added “${r.displayName}” to the research queue.`
        : `“${r.displayName}” is already in the queue.`
    );
    setQueueErr("");
    setQueueOpen(true);
  };

  const queueSelected = () => {
    if (!selected) return;
    const ok = addToQueue({
      name: selected.name || "",
      brand: selected.brandName || "",
      upc: upcOf(selected),
      refCode: selectedRefCode || undefined,
      id: selected.id,
    });
    setQueueMsg(
      ok
        ? `Added “${selected.name}” to the research queue.`
        : `“${selected.name}” is already in the queue.`
    );
    setQueueErr("");
    setQueueOpen(true);
  };

  const updateQueueItem = (key: string, patch: Partial<QueueItem>) =>
    setAndSaveQueue(queue.map((q) => (q.key === key ? { ...q, ...patch } : q)));

  const removeQueueItem = (key: string) =>
    setAndSaveQueue(queue.filter((q) => q.key !== key));

  const submitQueue = async () => {
    const valid = queue.filter((q) => q.name.trim() || q.upc.trim());
    if (valid.length === 0) {
      setQueueErr("Add at least one item (with a name or UPC) to research.");
      return;
    }
    setQueueErr("");
    setQueueMsg("");
    setSubmitting(true);
    try {
      await onSubmit(buildQueueInput(valid), valid.length);
      setAndSaveQueue([]);
      setQueueMsg(
        "Research started — it runs in the background. Track it in the Ingest runs panel (bottom-right); results open in the full review & merge page."
      );
    } catch (err) {
      const e = err as Error;
      setQueueErr(
        e?.name === "IngestJobLimitError"
          ? e.message
          : e?.message || "Failed to start research."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const recheck = async () => {
    if (!draft) return;
    setRechecking(true);
    setSaveErr("");
    setSaveMsg("");
    try {
      const res = await api.recheckConcerns(draft);
      setDraft(res.food);
      setDirtySinceRecheck(false);
      setSaveMsg(
        res.blocking
          ? "Re-checked — still has blocking concern codes."
          : "Re-checked — no blocking concerns."
      );
    } catch (err) {
      setSaveErr(extractApiError(err, "Re-check failed"));
    } finally {
      setRechecking(false);
    }
  };

  // Auto re-run the sanity checks when the inline editor is closed, if the
  // record changed since the last check.
  const handleEditingChange = (isEditing: boolean) => {
    if (!isEditing && dirtySinceRecheck && !rechecking) void recheck();
  };

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageResults = useMemo(
    () => results.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE),
    [results, page]
  );

  const runSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    setSearchError("");
    setPage(0);
    try {
      const found = await api.searchFoods(query.trim());
      setResults(found);
      if (found.length === 0) setSearchError("No results.");
    } catch (err) {
      setResults([]);
      setSearchError(extractApiError(err, "Search failed"));
    } finally {
      setSearching(false);
    }
  };

  // Search results are lightweight summaries; load the full editable record on
  // demand via the item's refCode.
  const select = async (r: CatalogSearchResult) => {
    setSelectedRefCode(r.refCode);
    setLoadingDetail(true);
    setDetailError("");
    setSelected(null);
    setDraft(null);
    setSaveMsg("");
    setSaveErr("");
    try {
      const full = await api.getFoodByRefCode(r.refCode);
      if (!full) {
        setDetailError("Could not load full record for this item.");
        return;
      }
      setSelected(full);
      setDraft(full);
      setDirtySinceRecheck(false);
    } catch (err) {
      setDetailError(extractApiError(err, "Failed to load product"));
    } finally {
      setLoadingDetail(false);
    }
  };

  const save = async () => {
    if (!selected || !draft || !selected.id) return;
    setSaving(true);
    setSaveMsg("");
    setSaveErr("");
    try {
      const updated: PassioFood = { ...draft, id: selected.id };
      const result = await api.updateFood(updated);
      await api.notifyChange({
        action: "update",
        food: result,
        original: selected,
        customerName,
      });
      setSelected(result);
      setDraft(result);
      setResults((rs) =>
        rs.map((r) =>
          r.refCode === selectedRefCode
            ? {
                ...r,
                displayName: result.name || r.displayName,
                brandName: result.brandName || r.brandName,
                tags: result.tags || r.tags,
              }
            : r
        )
      );
      setSaveMsg("Saved and ops notified.");
    } catch (err) {
      setSaveErr(extractApiError(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const queuedKeys = new Set(
    queue.map((q) => q.refCode || q.id || q.upc.trim())
  );
  const isQueued = (r: CatalogSearchResult): boolean =>
    queuedKeys.has(r.refCode) ||
    (!!r.productCode && queuedKeys.has(r.productCode.trim()));

  return (
    <div className="space-y-5">
      {/* ===== Research queue bar (spans full width) ===== */}
      <div className="rounded-lg border border-indigo-200 bg-indigo-50/60">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => setQueueOpen((o) => !o)}
            className="flex items-center gap-2 text-left"
          >
            <span className="text-sm font-semibold text-indigo-900">
              Research queue
            </span>
            <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-semibold text-white">
              {queue.length}
            </span>
            <span className="text-xs text-indigo-500">
              {queueOpen ? "▾" : "▸"}
            </span>
          </button>
          <div className="flex items-center gap-2">
            {queue.length > 0 && (
              <SecondaryButton
                type="button"
                onClick={() => setAndSaveQueue([])}
                disabled={submitting}
              >
                Clear
              </SecondaryButton>
            )}
            <PrimaryButton
              type="button"
              onClick={submitQueue}
              disabled={
                submitting || queue.length === 0 || atCapacity || !ingestChainId
              }
            >
              {submitting
                ? "Starting…"
                : queue.length === 0
                ? "Research queue"
                : `Research ${queue.length} ${
                    queue.length === 1 ? "item" : "items"
                  } →`}
            </PrimaryButton>
          </div>
        </div>

        {queueOpen && (
          <div className="border-t border-indigo-100 px-4 py-3">
            <p className="mb-2 text-xs text-indigo-700">
              Queue any products you want to refresh, then research them all in
              one background run. Each is re-researched against the latest web
              data and, because it already exists, opens in the full review
              &amp; merge page — where you can edit every field and clear
              blocking concerns before writing.
            </p>
            {queue.length === 0 ? (
              <div className="rounded-md border border-dashed border-indigo-200 bg-white/60 p-3 text-center text-xs text-indigo-500">
                Nothing queued yet. Use{" "}
                <span className="font-medium">＋ Queue</span> on a search
                result, or “Queue for research” after opening a product.
              </div>
            ) : (
              <div className="space-y-2">
                {/* Column headers */}
                <div className="hidden grid-cols-12 gap-2 px-1 text-[11px] font-medium text-indigo-500 sm:grid">
                  <div className="col-span-5">Name</div>
                  <div className="col-span-4">Brand</div>
                  <div className="col-span-2">UPC</div>
                  <div className="col-span-1" />
                </div>
                {queue.map((q) => (
                  <div key={q.key} className="grid grid-cols-12 gap-2">
                    <input
                      className="col-span-12 rounded-md border border-indigo-200 bg-white px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none sm:col-span-5"
                      placeholder="Product name"
                      value={q.name}
                      disabled={submitting}
                      onChange={(e) =>
                        updateQueueItem(q.key, { name: e.target.value })
                      }
                    />
                    <input
                      className="col-span-7 rounded-md border border-indigo-200 bg-white px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none sm:col-span-4"
                      placeholder="Brand"
                      value={q.brand}
                      disabled={submitting}
                      onChange={(e) =>
                        updateQueueItem(q.key, { brand: e.target.value })
                      }
                    />
                    <input
                      className="col-span-4 rounded-md border border-indigo-200 bg-white px-2 py-1.5 text-sm focus:border-indigo-500 focus:outline-none sm:col-span-2"
                      placeholder="UPC"
                      value={q.upc}
                      disabled={submitting}
                      onChange={(e) =>
                        updateQueueItem(q.key, { upc: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      className="col-span-1 rounded-md text-indigo-400 hover:text-red-600 disabled:opacity-40"
                      disabled={submitting}
                      onClick={() => removeQueueItem(q.key)}
                      aria-label="Remove from queue"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {atCapacity && ingestChainId && (
              <p className="mt-2 text-xs font-medium text-amber-700">
                You have {MAX_CONCURRENT_JOBS} runs going — wait for one to
                finish (or clear it) in the Ingest runs panel before starting
                another.
              </p>
            )}
            {!ingestChainId && (
              <p className="mt-2 text-xs font-medium text-amber-700">
                No ingest chain id configured — set it in settings (top-right
                gear).
              </p>
            )}
            {queueErr && (
              <p className="mt-2 text-xs font-medium text-red-600">
                {queueErr}
              </p>
            )}
            {queueMsg && !queueErr && (
              <p className="mt-2 text-xs text-green-700">{queueMsg}</p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Search column */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Search the catalog
          </h3>
          <div className="mt-2 flex gap-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="e.g. keto protein bar"
              className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
            />
            <PrimaryButton
              type="button"
              onClick={runSearch}
              disabled={searching}
            >
              {searching ? "Searching…" : "Search"}
            </PrimaryButton>
          </div>
          {searchError && (
            <div className="mt-2 text-sm text-gray-500">{searchError}</div>
          )}

          {results.length > 0 && (
            <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
              <span>
                {results.length} result{results.length === 1 ? "" : "s"} ·
                showing {page * PAGE_SIZE + 1}–
                {Math.min(results.length, page * PAGE_SIZE + PAGE_SIZE)}
              </span>
              <div className="flex items-center gap-2">
                <SecondaryButton
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  Prev
                </SecondaryButton>
                <span>
                  {page + 1} / {totalPages}
                </span>
                <SecondaryButton
                  type="button"
                  onClick={() =>
                    setPage((p) => Math.min(totalPages - 1, p + 1))
                  }
                  disabled={page >= totalPages - 1}
                >
                  Next
                </SecondaryButton>
              </div>
            </div>
          )}

          <div className="mt-3 max-h-[32rem] space-y-2 overflow-auto pr-1">
            {pageResults.map((r) => {
              const queued = isQueued(r);
              return (
                <div
                  key={r.refCode}
                  className={`rounded-md border p-3 transition ${
                    selectedRefCode === r.refCode
                      ? "border-indigo-400 bg-indigo-50"
                      : "border-gray-200 bg-white hover:border-indigo-300"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => select(r)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-gray-900">
                          {r.displayName}
                        </span>
                        {typeof r.score === "number" && (
                          <Badge tone="gray">{r.score.toFixed(2)}</Badge>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {r.brandName || "—"}
                        {r.productCode ? ` · UPC ${r.productCode}` : ""}
                        {r.type ? ` · ${r.type}` : ""}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => queueSearchResult(r)}
                      disabled={queued}
                      className={`flex-shrink-0 rounded-md px-2 py-1 text-xs font-semibold ${
                        queued
                          ? "cursor-default bg-green-100 text-green-700"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      }`}
                      title="Add to the research queue"
                    >
                      {queued ? "✓ Queued" : "＋ Queue"}
                    </button>
                  </div>
                  {((r.tags && r.tags.length > 0) ||
                    (r.concerns && r.concerns.length > 0)) && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(r.tags || []).slice(0, 4).map((t) => (
                        <Badge key={t} tone="blue">
                          {t}
                        </Badge>
                      ))}
                      {r.concerns && r.concerns.length > 0 && (
                        <Badge tone="amber">
                          {r.concerns.length} concern(s)
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Editor column */}
        <div>
          {loadingDetail ? (
            <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
              <Spinner label="Loading full product record…" />
            </div>
          ) : detailError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-8 text-center text-sm text-red-600">
              {detailError}
            </div>
          ) : !selected || !draft ? (
            <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
              Pick a product to edit any field (incl. nutrients &amp; portions)
              directly, or add it to the research queue to refresh it against
              the latest online data.
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Edit product
                </h3>
                <span className="text-xs text-gray-400">id {selected.id}</span>
              </div>

              {/* Single view/edit widget: toggle "Edit" to modify any field
                  (incl. nutrients & portions); macros preview live. */}
              <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-3">
                <FoodDetailPanel
                  food={draft}
                  onChange={(next) => {
                    setDraft(next);
                    setDirtySinceRecheck(true);
                  }}
                  onEditingChange={handleEditingChange}
                  onRecheck={recheck}
                  rechecking={rechecking}
                />
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <PrimaryButton type="button" onClick={save} disabled={saving}>
                  {saving ? "Saving…" : "Save changes"}
                </PrimaryButton>
                <SecondaryButton type="button" onClick={queueSelected}>
                  ＋ Queue for research
                </SecondaryButton>
              </div>
              <p className="mt-1.5 text-xs text-gray-500">
                <span className="font-medium">Queue for research</span> adds
                this product to the research queue above. Submit the queue to
                re-research it against the latest web sources in a background
                run — the results open in the full review &amp; merge page where
                you can edit fields and resolve concerns before writing.
              </p>

              {saveMsg && (
                <div className="mt-2 text-sm text-green-700">{saveMsg}</div>
              )}
              {saveErr && (
                <div className="mt-2 text-sm text-red-600">{saveErr}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchEditTab;
